import { CircularProgress, Dialog, DialogContent } from "@mui/material"
import { PDFViewer } from "@react-pdf/renderer"
import { Examination, Project, Report } from "graphql/types"
import { useStoreState } from "hooks/storeHooks"
import { useCustomerByBusinessAccount } from "hooks/useCustomerData"
import { useEffect, useMemo, useState } from "react"
import { enhanceReportWithProfessionalFormat } from "store/buildReportIntegration"
import { getImageAsBase64 } from "utils/getFileURL"
import { PDFDocument } from "./PDFDocument"

// Polyfill for Buffer (needed for @react-pdf/renderer in browser)
if (typeof window !== "undefined" && !window.Buffer) {
  window.Buffer = {
    isBuffer: () => false,
  } as any
}

type PDFExportButtonProps = {
  report: Report
  open: boolean
  onClose: () => void
  fileName?: string
  variant?: "icon" | "button"
  project?: Project
  examination?: Examination // Single examination for backward compatibility
  examinations?: Examination[] // Multiple examinations for multi-test reports
  useProfessionalFormat?: boolean
  isPreview?: boolean
}

export const PDFExportDialog = (props: PDFExportButtonProps) => {
  const {
    report,
    project,
    examination,
    examinations,
    useProfessionalFormat = true,
    isPreview = false,
    open,
    onClose,
  } = props

  // Fetch customer data based on businessAccount; derive from leads if missing on project
  const leads = useStoreState((s) => s.leads)
  const businessAccount = (() => {
    if (project?.lead?.businessAccount) return project.lead.businessAccount
    const oppId = project?.lead?.opportunityID || project?.lead?.id
    if (!oppId) return undefined
    const lead = leads.find((l) => l.opportunityID === oppId)
    return lead?.businessAccount || undefined
  })()
  const { customer, loading: customerLoading } = useCustomerByBusinessAccount(businessAccount)

  // Removed verbose debug logging
  const [loadingImages, setLoadingImages] = useState(false)
  const [base64Images, setBase64Images] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    if (open) {
      handleOpen()
    }
  }, [open])

  // Enhance report with professional format if requested and project is available
  const enhancedReport = useMemo(() => {
    let processedReport = report
    if (useProfessionalFormat && project) {
      // Use examinations array if provided, otherwise create array from single examination
      const examinationArray = examinations || (examination ? [examination] : [])

      processedReport = enhanceReportWithProfessionalFormat(
        report,
        project,
        examinationArray,
        customer
      )
    }
    // Set watermark based on preview mode
    // Preview always shows watermark, PDF export never shows it (unless explicitly needed in the future)
    return {
      ...processedReport,
      showDraftWatermark: isPreview,
    }
  }, [report, project, examination, examinations, useProfessionalFormat, isPreview, customer])

  // Function to collect all image IDs from the report
  const collectImageIds = (report: Report): string[] => {
    const imageIds: string[] = []

    const processChapter = (chapter: any, depth = 0) => {
      if (!chapter) return

      // Process data nodes in chapter
      if (chapter.data) {
        chapter.data.forEach((node: any) => {
          if (node.image && Array.isArray(node.image)) {
            node.image.forEach((img: any) => {
              if (img?.id) {
                imageIds.push(img.id)
              }
            })
          }
        })
      }

      // Process subchapters recursively
      if (chapter.subChapter && Array.isArray(chapter.subChapter)) {
        chapter.subChapter.forEach((ch: any) => processChapter(ch, depth + 1))
      }
    }

    // Process all chapters
    if (report.chapter && Array.isArray(report.chapter)) {
      report.chapter.forEach(processChapter)
    }

    const uniqueIds = Array.from(new Set(imageIds)) // Remove duplicates

    // Image collection completed

    return uniqueIds
  }

  // Pre-load images when dialog opens
  const handleOpen = async () => {
    setLoadingImages(true)

    // Collect all image IDs from the report
    const imageIds = collectImageIds(enhancedReport)

    // Removed debug logging for handleOpen

    // Load images in parallel
    const imagePromises = imageIds.map(async (id) => {
      const base64 = await getImageAsBase64(id)
      // Base64 conversion completed
      return { id, base64 }
    })

    try {
      const results = await Promise.all(imagePromises)
      const imageMap: { [key: string]: string } = {}

      let failCount = 0

      results.forEach(({ id, base64 }) => {
        if (base64) {
          imageMap[id] = base64
        } else {
          failCount++
        }
      })

      if (failCount > 0) {
        console.warn(`Warning: ${failCount} images failed to load`)
      }

      setBase64Images(imageMap)
    } catch (error) {
      console.error("Error loading images for PDF:", error)
    } finally {
      setLoadingImages(false)
    }
  }

  const handleClose = () => {
    onClose()
    setBase64Images({}) // Clear images when closing
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ height: "100vh", overflow: "hidden" }}>
        {loadingImages || customerLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
            <span style={{ marginLeft: "10px" }}>
              {customerLoading ? "Loading customer data..." : "Loading images..."}
            </span>
          </div>
        ) : (
          <PDFViewer width="100%" height="100%">
            <PDFDocument report={enhancedReport} base64Images={base64Images} />
          </PDFViewer>
        )}
      </DialogContent>
    </Dialog>
  )
}
